'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { BackerRewardsHowItWorksModal } from '@/components/how-it-works/backer-rewards-how-it-works-modal';
import { HowItWorksInfoButton } from '@/components/how-it-works/how-it-works-info-button';
import { type BackerRewardProjectionUnavailableReason, projectBackerReward } from '@/components/launchpad/backer-reward-projection';
import { AnimatedElasticSlider } from '@/components/ui/animated-elastic-slider';
import { InputField } from '@/components/ui/input-field';
import { Panel, PanelContent } from '@/components/ui/panel';
import { D6_SCALE } from '@/config/solana';
import { onEvent } from '@/events';
import { useFormattedCurrency } from '@/lib/currency';
import { logger } from '@/lib/telemetry/logger';
const SLIDER_MIN = 0;
const SLIDER_MAX = 5_000_000;
const SLIDER_STEP = 500_000;
const SLIDER_DEFAULT: number = SLIDER_STEP * 2;
const loggedUnavailableReasons: Set<BackerRewardProjectionUnavailableReason> = new Set();
export interface ProposalBackerRewardsPanelProps {
  proposalPubkey: string;
  amountInput: string;
  currentTotalBackedD6: string;
  viewerBackerShareBalanceD6: string;
  creationThresholdD6: string | undefined;
  feeBpsBuy: number | undefined;
  feeBpsSellNormal: number | undefined;
  feeSplitBackersBps: number | undefined;
  configStatus: 'loading' | 'ready' | 'unavailable';
  showBackingInput?: boolean;
  onAmountInputChange?: (next: string) => void;
  bare?: boolean;
}
export function ProposalBackerRewardsPanel(t0) {
  const $ = _c(60);
  const {
    proposalPubkey,
    amountInput,
    currentTotalBackedD6,
    viewerBackerShareBalanceD6,
    creationThresholdD6,
    feeBpsBuy,
    feeBpsSellNormal,
    feeSplitBackersBps,
    configStatus,
    showBackingInput: t1,
    onAmountInputChange,
    bare: t2
  } = t0;
  const showBackingInput = t1 === undefined ? false : t1;
  const bare = t2 === undefined ? false : t2;
  const t = useTranslations("launchpad.detail");
  const {
    formatBalance,
    toUsdc,
    currencySymbol
  } = useFormattedCurrency();
  const [volumeUsdc, setVolumeUsdc] = useState(SLIDER_DEFAULT);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  let t3;
  let t4;
  if ($[0] !== proposalPubkey) {
    t3 = () => {
      onEvent("creator_rewards_panel_viewed", {
        proposalId: proposalPubkey
      });
    };
    t4 = [proposalPubkey];
    $[0] = proposalPubkey;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  const newBackingAmountD6 = localAmountToD6(amountInput, toUsdc);
  const missingConfig = configStatus !== "ready" || creationThresholdD6 === undefined || feeBpsBuy === undefined || feeBpsSellNormal === undefined || feeSplitBackersBps === undefined;
  const projection = configStatus === "loading" ? null : missingConfig ? {
    status: "unavailable",
    reason: "missing_config"
  } : projectBackerReward({
    currentTotalBackedD6,
    viewerBackerShareBalanceD6,
    newBackingAmountD6,
    creationThresholdD6,
    feeBpsBuy,
    feeBpsSellNormal,
    feeSplitBackersBps,
    volumeD6: BigInt(volumeUsdc) * BigInt(D6_SCALE)
  });
  const unavailableReason = projection?.status === "unavailable" ? projection.reason : undefined;
  useEffect(() => {
    if (unavailableReason === undefined) {
      return;
    }
    if (loggedUnavailableReasons.has(unavailableReason)) {
      return;
    }
    loggedUnavailableReasons.add(unavailableReason);
    logger.warn("launchpad.backer_reward_projection_unavailable", {
      reason: unavailableReason
    });
  }, [unavailableReason]);
  const scenarioBackingD6 = projection?.status === "ready" ? projection.userSharesAfterD6 : newBackingAmountD6;
  let t5;
  if ($[3] !== onAmountInputChange) {
    t5 = function handleStandaloneAmountChange(event) {
      const raw = event.currentTarget.value;
      if (/^\d*\.?\d{0,2}$/.test(raw) && raw.replace(".", "").length <= 12) {
        onAmountInputChange?.(raw);
      }
    };
    $[3] = onAmountInputChange;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleStandaloneAmountChange = t5;
  const t6 = "flex flex-col gap-4";
  const t7 = "flex items-start justify-between gap-2";
  const t8 = "flex min-w-0 flex-col gap-1";
  let t9;
  if ($[5] !== t) {
    t9 = t("backerRewards.heading");
    $[5] = t;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== t9) {
    t10 = <h3 className="text-body font-semibold text-foreground-primary">{t9}</h3>;
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const t11 = "text-heading-md font-bold leading-none text-foreground-primary tabular-nums";
  const t12 = projectionLabel({
    projection,
    configStatus,
    formatBalance,
    loadingLabel: t("backerRewards.loading"),
    unavailableLabel: t("backerRewards.unavailable")
  });
  let t13;
  if ($[9] !== t12) {
    t13 = <span className={t11}>{t12}</span>;
    $[9] = t12;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] !== formatBalance || $[12] !== projection?.status || $[13] !== scenarioBackingD6 || $[14] !== t || $[15] !== volumeUsdc) {
    t14 = projection?.status === "ready" ? t("backerRewards.scenarioTemplate", {
      amount: formatBalance(Number(scenarioBackingD6) / D6_SCALE),
      volume: formatBalance(volumeUsdc, "compact")
    }) : t("backerRewards.empty");
    $[11] = formatBalance;
    $[12] = projection?.status;
    $[13] = scenarioBackingD6;
    $[14] = t;
    $[15] = volumeUsdc;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t14) {
    t15 = <span className="text-micro text-foreground-secondary">{t14}</span>;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== t10 || $[20] !== t13 || $[21] !== t15) {
    t16 = <div className={t8}>{t10}{t13}{t15}</div>;
    $[19] = t10;
    $[20] = t13;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setHowItWorksOpen(true);
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== t) {
    t18 = t("backerRewards.infoLabel");
    $[24] = t;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t18) {
    t19 = <HowItWorksInfoButton onClick={t17} ariaLabel={t18} />;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t16 || $[29] !== t19) {
    t20 = <div className={t7}>{t16}{t19}</div>;
    $[28] = t16;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== amountInput || $[32] !== currencySymbol || $[33] !== handleStandaloneAmountChange || $[34] !== onAmountInputChange || $[35] !== showBackingInput || $[36] !== t) {
    t21 = showBackingInput && onAmountInputChange !== undefined ? <InputField value={amountInput} onChange={handleStandaloneAmountChange} inputMode="decimal" label={t("backerRewards.inputLabel")} placeholder={t("backerRewards.inputPlaceholder")} prefix={currencySymbol} fullWidth={true} /> : null;
    $[31] = amountInput;
    $[32] = currencySymbol;
    $[33] = handleStandaloneAmountChange;
    $[34] = onAmountInputChange;
    $[35] = showBackingInput;
    $[36] = t;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t) {
    t22 = t("backerRewards.sliderLabel");
    $[38] = t;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t) {
    t23 = t("backerRewards.sliderAriaLabel");
    $[40] = t;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== formatBalance) {
    t24 = value => formatBalance(value, "compact");
    $[42] = formatBalance;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t22 || $[45] !== t23 || $[46] !== t24 || $[47] !== volumeUsdc) {
    t25 = <AnimatedElasticSlider label={t22} aria-label={t23} min={SLIDER_MIN} max={SLIDER_MAX} step={SLIDER_STEP} value={volumeUsdc} onValueChange={setVolumeUsdc} formatValue={t24} />;
    $[44] = t22;
    $[45] = t23;
    $[46] = t24;
    $[47] = volumeUsdc;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t20 || $[50] !== t21 || $[51] !== t25) {
    t26 = <div className={t6}>{t20}{t21}{t25}</div>;
    $[49] = t20;
    $[50] = t21;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== howItWorksOpen) {
    t27 = <BackerRewardsHowItWorksModal open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />;
    $[53] = howItWorksOpen;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t26 || $[56] !== t27) {
    t28 = <>{t26}{t27}</>;
    $[55] = t26;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  const content = t28;
  if (bare) {
    return content;
  }
  let t29;
  if ($[58] !== content) {
    t29 = <Panel density="compact" className="overflow-hidden"><PanelContent>{content}</PanelContent></Panel>;
    $[58] = content;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  return t29;
}
interface ProjectionLabelInput {
  projection: ReturnType<typeof projectBackerReward> | null;
  configStatus: ProposalBackerRewardsPanelProps['configStatus'];
  formatBalance: (value: number) => string;
  loadingLabel: string;
  unavailableLabel: string;
}
function projectionLabel({
  projection,
  configStatus,
  formatBalance,
  loadingLabel,
  unavailableLabel
}: ProjectionLabelInput): string {
  if (configStatus === 'loading') return loadingLabel;
  if (projection === null || projection.status === 'unavailable') {
    return unavailableLabel;
  }
  if (projection.status === 'empty') return formatBalance(0);
  const lower = formatBalance(Number(projection.lowerRewardD6) / D6_SCALE);
  if (projection.lowerRewardD6 === projection.upperRewardD6) return lower;
  return `${lower}–${formatBalance(Number(projection.upperRewardD6) / D6_SCALE)}`;
}
function localAmountToD6(amountInput: string, toUsdc: (amount: number) => number): bigint {
  const localAmount = Number.parseFloat(amountInput);
  if (!Number.isFinite(localAmount) || localAmount <= 0) return BigInt(0);
  const amountD6 = Math.floor(toUsdc(localAmount) * D6_SCALE);
  if (!Number.isSafeInteger(amountD6) || amountD6 <= 0) return BigInt(0);
  return BigInt(amountD6);
}